import { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser, signOut } from 'firebase/auth';
import { auth, getDocument } from '../services/firebase';
import { User } from '@/features/users';
import { devError } from '../util/devLog';

interface AuthUser extends FirebaseUser {
	id: string;
	name: string;
	role: string; // "admin", "user", "web"
}

interface AuthContextType {
	user: AuthUser | null;
	loading: boolean;
	completeLogin: (firebaseUser: FirebaseUser) => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function determineRole(userDoc: User): string {
	if (userDoc.type === 'admin') return 'admin';
	if (userDoc.type === 'user' && userDoc.userType === 'internal_worker_with_web') return 'web';
	return 'user';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
			try {
				setLoading(true);
				if (firebaseUser) {
					const userDoc = await getDocument<User>(firebaseUser.uid, 'users');
					if (userDoc != null) {
						setUser({ ...firebaseUser, id: firebaseUser.uid, name: userDoc.name, role: determineRole(userDoc) });
					}
				} else {
					setUser(null);
				}
			} catch (error) {
				devError('Error getting user:', error);
				setUser(null);
			} finally {
				setLoading(false);
			}
		});

		return () => unsubscribe();
	}, []);

	const completeLogin = async (firebaseUser: FirebaseUser) => {
		try {
			const userDoc = await getDocument<User>(firebaseUser.uid, 'users');
			if (userDoc != null) {
				setUser({ ...firebaseUser, id: firebaseUser.uid, name: userDoc.name, role: determineRole(userDoc) });
			}
		} catch (error) {
			devError('Error getting user:', error);
			setUser(null);
		}
	};

	const logout = async () => {
		await signOut(auth);
		setUser(null);
	};

	return <AuthContext.Provider value={{ user, loading, logout, completeLogin }}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
}
